import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  numPages: 0,
  zoom: 700,
  page: '1',
  scroll: 0,
  scrollToPage: null,
  pdfDoc: null,
  positions: {},
  participants: [],
  currentParticipant: null,
  documentInfo: {},
  dimenssions: { width: 0, height: 0 },
  isLoadingDoc: false,
  steps: {
    typeSign: null,
    withInvites: null
  },
  loaderMessage: '',
  isSignatureLinePositioned: false,
  signaturePositions: [],
  isSignaturePositionModeActive: false,
  hasExistingSignatureLines: false,
  usingExistingSignatureLine: false,
  ignoreSignatureLines: false,
  isShared: false,
  validInvitesAdded: false,
  invitationConfig: {}, // { [documentId]: { allowDownload, allowView, ignore } }
  emailErrors: {},
  isSendingInvites: false
};

// util interno para quitar no-serializables
const sanitizeDoc = (doc = {}) => {
  const out = { ...doc };
  // remueve props conocidas
  delete out.cloudPDF;

  // remueve cualquier ArrayBuffer o TypedArray suelto
  for (const k of Object.keys(out)) {
    const v = out[k];
    if (v instanceof ArrayBuffer || ArrayBuffer.isView?.(v)) {
      delete out[k];
    }
  }
  return out;
};

const documentSlice = createSlice({
  name: 'document',
  initialState,
  reducers: {
    setNumPages: (state, action) => {
      state.numPages = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setZoom: (state, action) => {
      state.zoom = action.payload;
    },
    setScroll: (state, action) => {
      state.scroll = state.zoom * action.payload;
    },
    nextPage: (state) => {
      state.scroll = state.scroll + state.dimenssions.height;
    },
    prevPage: (state) => {
      state.scroll = state.scroll - state.dimenssions.height;
    },
    addPdfDoc: (state, action) => {
      state.pdfDoc = action.payload;
    },
    setInitialPositions: (state, action) => {
      let initialPositions = {};
      for (let i = 1; i <= action.payload; i++) {
        initialPositions = {
          ...initialPositions,
          [i]: [
            {
              x: 300,
              y: 600,
              email: 'example@gmail.com',
              signed: false,
              page: i
            }
          ]
        };
      }
      state.positions = initialPositions;
    },
    addParticipant: (state, action) => {
      state.participants = action.payload;
    },
    addOneParticipant: (state, action) => {
      state.participants = [...state.participants, action.payload];
    },
    addParticipants: (state, action) => {
      // Reemplaza completamente la lista para evitar duplicados, 
      // asumiendo que el payload trae la lista completa y actualizada.
      state.participants = action.payload;
    },
    removeParticipant: (state, action) => {
      state.participants = state.participants.filter(
        (part) => part?.id !== action.payload
      );
    },
    asignAllPages: (state, action) => {
      const { email, type, ids, positionsArray, isOwners } = action.payload;
      for (let i = 1; i <= state.numPages; i++) {
        let prevData = state.positions[i] ? [...state.positions[i]] : [];
        prevData = prevData.filter(pos => pos.email !== email || pos.type !== type);
        prevData.push({
          email,
          x: positionsArray[i - 1].x, // Usamos la posición calculada
          y: positionsArray[i - 1].y, // Usamos la posición calculada
          signed: false,
          page: i,
          type,
          id: ids[i - 1],
          isOwner: isOwners[i - 1]
        });
        state.positions[i] = prevData;
      }

      state.participants = state.participants.map((part) => {
        if (part.email === email) {
          return { ...part, asignedAll: true, pagesAsigned: 'Todas' };
        }
        return part;
      });
      
      // Update currentParticipant if it matches
      if (state.currentParticipant && state.currentParticipant.email === email) {
          state.currentParticipant = { ...state.currentParticipant, asignedAll: true, pagesAsigned: 'Todas' };
      }
    },
    removeAllPages: (state, action) => {
      for (const page in state.positions) {
        state.positions[page] = state.positions[page].filter(
          (p) => p.email !== action.payload.email || p.type !== action.payload.type
        );
      }
      state.participants = state.participants.map((par) => {
        if (par.email === action.payload.email) {
          return { ...par, asignedAll: false };
        } else {
          return par;
        }
      });
      
      // Update currentParticipant if it matches
      if (state.currentParticipant && state.currentParticipant.email === action.payload.email) {
          state.currentParticipant = { ...state.currentParticipant, asignedAll: false };
      }
    },
    asignCurrentPage: (state, action) => {
      const { email, type, page, x, y, id, isOwner } = action.payload;
      if (state.positions[page]) {
        let prevData = state.positions[page] ? [...state.positions[page]] : [];
        prevData = prevData.filter(pos => pos.email !== email || pos.type !== type);
        prevData.push({
          email,
          x,
          y,
          signed: false,
          page,
          type,
          id,
          isOwner
        });
        state.positions[page] = prevData;
      } else {
        state.positions[page] = [{
          email,
          x,
          y,
          signed: false,
          page,
          type,
          id,
          isOwner
        }];
      }
    },
    unassignCuttentPage: (state, action) => {
      const pageKey = state.page; // o action.payload.page si lo envías
      if (state.positions[pageKey]) {
        state.positions[pageKey] = state.positions[pageKey].filter(
          (pos) => pos && (pos.email !== action.payload.email || pos.type !== action.payload.type)
        );
        // Si el array quedó vacío, asegúrate de asignarlo como [] (no undefined)
        if (state.positions[pageKey].length === 0) {
          state.positions[pageKey] = [];
        }
      } else {
        state.positions[pageKey] = [];
      }
      state.participants = state.participants.map((par) => {
        if (par.email === action.payload.email) {
          return { ...par, asignedAll: false };
        }
        return par;
      });
    },
    addCurrentParticipant: (state, action) => {
      state.currentParticipant = action.payload;
    },
    updatePositionParticipant: (state, action) => {
      const { page, participant, x, y, type, signed, id, rotation } = action.payload;
      if (!state.positions[page]) {
        state.positions[page] = [];
      }
      const index = state.positions[page].findIndex(pos => pos.email === participant.email && pos.type === type);
      if (index !== -1) {
        // Actualizamos la posición existente
        state.positions[page][index] = { ...state.positions[page][index], x, y, signed, id, rotation };
      } else {
        // Añadimos la nueva posición con ID
        state.positions[page].push({ email: participant.email, x, y, type, signed, id, rotation });
      }
    },
    sign: (state, action) => {
      state.positions[action.payload.page] = state.positions[
        action.payload.page
      ].map((pos) => {
        if (pos.email === action.payload.email) {
          return { ...pos, signed: true };
        } else {
          return pos;
        }
      });
    },
    addDocumentInfo: {
      reducer: (state, action) => {
        const doc = action.payload;
        state.documentInfo = {
          ...doc,
          allowDownload: !!doc.allowDownload,
          allowView: !!doc.allowView,
          ignoreSignatureLines: !!doc.ignoreSignatureLines
        };
      },
      // esto corre ANTES del middleware de serializables
      prepare: (doc) => ({ payload: sanitizeDoc(doc) })
    },
    cleanPositions: (state, action) => {
      state.positions = {};
    },
    setDimessions: (state, action) => {
      state.dimenssions = action.payload;
    },
    setIsLoadingDoc: (state, action) => {
      state.isLoadingDoc = action.payload;
    },
    editTypeOfSignOwner: (state, action) => {
      state.documentInfo = {
        ...state.documentInfo,
        typeOfSignOwner: action.payload
      };
    },
    updateEmailPositionsParticipant: (state, action) => {
      for (const pos in state.positions) {
        const existInThisPosition = state.positions[pos].some(
          ({ email }) => email === action.payload.email
        );
        if (existInThisPosition) {
          const newPosUpdated = state.positions[pos].map((pos) => {
            if (pos.email === action.payload.email) {
              return { ...pos, email: action.payload.newEmail };
            } else {
              return pos;
            }
          });
          state.positions[pos] = newPosUpdated;
        }
      }
    },
    setStepWithInvites: (state, action) => {
      state.steps.withInvites = action.payload;
    },
    setStepTypeSign: (state, action) => {
      state.steps.typeSign = action.payload;
    },
    cleanSteps: (state) => {
      state.steps = {};
    },
    setLoaderMessage: (state, action) => {
      state.loaderMessage = action.payload;
    },
    resetDocumentInfo: (state) => {
      state.documentInfo = {};
      state.signaturePositions = [];
      state.isSignaturePositionModeActive = false;
      state.hasExistingSignatureLines = false;
      state.usingExistingSignatureLine = false;
      state.ignoreSignatureLines = false;
      state.numPages = 0;
      state.zoom = 700;
      state.page = '1';
      state.scroll = 0;
      state.pdfDoc = null;
      state.positions = {};
      state.participants = [];
      state.currentParticipant = null;
      state.dimenssions = { width: 0, height: 0 };
      state.isLoadingDoc = false;
      state.steps = {
        typeSign: null,
        withInvites: null
      };
      state.loaderMessage = '';
      state.isSignatureLinePositioned = false;
    },
    setSignatureLinePositioned: (state, action) => {
      state.isSignatureLinePositioned = action.payload;
    },
    addSignaturePosition: (state, action) => {
      if (!state.signaturePositions) {
        state.signaturePositions = [];
      }
      state.signaturePositions.push(action.payload);
    },
    removeSignaturePosition: (state, action) => {
      if (state.signaturePositions) {
        state.signaturePositions = state.signaturePositions.filter((pos, index) => index !== action.payload);
      }
    },
    setSignaturePositionMode: (state, action) => {
      state.isSignaturePositionModeActive = action.payload;
    },
    setHasExistingSignatureLines: (state, action) => {
      state.hasExistingSignatureLines = action.payload;
    },
    setUsingExistingSignatureLine: (state, action) => {
      state.usingExistingSignatureLine = action.payload;
    },
    setIgnoreSignatureLines: (state, action) => {
      state.ignoreSignatureLines = action.payload;
    },
    setSignaturePositions: (state, action) => {
      state.signaturePositions = action.payload;
    },
    setSharedView: (state, action) => {
      state.isShared = action.payload;
    },
    setValidInvitesAdded: (state, action) => {
      state.validInvitesAdded = action.payload;
    },
    setInvitationConfig: (state, action) => {
      const { documentId, config } = action.payload;
      state.invitationConfig[documentId] = config;
    },
    updateInvitationFlag: (state, action) => {
      const { documentId, flag, value } = action.payload;
      // Si aún no existe la configuración, la inicializamos
      if (!state.invitationConfig[documentId]) {
        state.invitationConfig[documentId] = { allowDownload: false, allowView: false, ignore: false };
      }
      state.invitationConfig[documentId][flag] = value;
    },
    setScrollToPage: (state, action) => {
      state.scrollToPage = action.payload;
    },
    setEmailErrors: (state, action) => {
      // action.payload debe ser un objeto con { signerId: errorMessage, ... }
      state.emailErrors = { ...action.payload };
    },
    clearEmailErrors: (state) => {
      state.emailErrors = {};
    },
    setIsSendingInvites: (state, action) => {
      state.isSendingInvites = action.payload;
    },
    updateSignaturePositionId: (state, action) => {
      const { tempId, realId } = action.payload;
      state.signaturePositions = state.signaturePositions.map(pos =>
        pos.id === tempId ? { ...pos, id: realId } : pos
      );
    },
    updatePositionId: (state, action) => {
      const { tempId, realId } = action.payload;
      for (const page in state.positions) {
        state.positions[page] = state.positions[page].map(pos =>
          pos.id === tempId ? { ...pos, id: realId } : pos
        );
      }
    },
    // 2) Elimina la posición con ese ID (temp o real) en caso de error
    removeSignaturePositionById: (state, action) => {
      const idToRemove = action.payload;
      state.signaturePositions = state.signaturePositions.filter(pos => pos.id !== idToRemove);
    },
    removePositionById: (state, action) => {
      const idToRemove = action.payload;
      for (const page in state.positions) {
        state.positions[page] = state.positions[page].filter(pos => pos.id !== idToRemove);
      }
    },
    // Marca en vivo (vía socket) que un participante rechazó firmar, sin tocar su `status` real.
    markParticipantRejected: (state, action) => {
      const { email, rejectedAt } = action.payload;
      state.participants = state.participants.map((part) => {
        if (part.email === email) {
          return { ...part, rejected_at: rejectedAt };
        }
        return part;
      });
    }
  }
});

export const {
  setNumPages,
  setPage,
  setZoom,
  setScroll,
  addPdfDoc,
  setInitialPositions,
  addParticipant,
  asignAllPages,
  asignCurrentPage,
  unassignCuttentPage,
  removeAllPages,
  addCurrentParticipant,
  updatePositionParticipant,
  sign,
  addDocumentInfo,
  addOneParticipant,
  removeParticipant,
  cleanPositions,
  setDimessions,
  addParticipants,
  nextPage,
  prevPage,
  setIsLoadingDoc,
  editTypeOfSignOwner,
  updateEmailPositionsParticipant,
  setStepWithInvites,
  setStepTypeSign,
  cleanSteps,
  setLoaderMessage,
  resetDocumentInfo,
  setSignatureLinePositioned,
  addSignaturePosition,
  removeSignaturePosition,
  setSignaturePositionMode,
  setHasExistingSignatureLines,
  setUsingExistingSignatureLine,
  setIgnoreSignatureLines,
  setSignaturePositions,
  setSharedView,
  setValidInvitesAdded,
  setInvitationConfig,
  updateInvitationFlag,
  setScrollToPage,
  setEmailErrors,
  clearEmailErrors,
  setIsSendingInvites,
  updateSignaturePositionId,
  updatePositionId,
  removeSignaturePositionById,
  removePositionById,
  markParticipantRejected
} = documentSlice.actions;

export default documentSlice.reducer;
